import React, { useEffect, useState } from "react";
import { Box, Collapse, Flex, HStack, Icon, Text } from "@chakra-ui/react";
import { formatDurationFromDeltaMs } from "../../../utils/helperFuncUtils";
import { AppIcons } from "constants/icons";
import { MarkdownRenderer } from "modules/common/MarkdownRenderer";
import { ReasoningGroup } from "../../interfaces";

export interface ThoughtBlockProps {
  group: ReasoningGroup;
  isActivelyStreaming: boolean;
}

// Collapsible row for a single LLM reasoning group inside the execution card.
const ThoughtBlock: React.FC<ThoughtBlockProps> = ({ group, isActivelyStreaming }) => {
  const [isExpanded, setIsExpanded] = useState(group.isOpen);
  const hasContent = group.text !== "";

  // Follow the isOpen flag while streaming so the block tracks live state.
  useEffect(() => {
    setIsExpanded(group.isOpen);
  }, [group.isOpen]);

  const durationLabel = group.durationMs !== undefined ? formatDurationFromDeltaMs(group.durationMs) : null;

  const label =
    isActivelyStreaming && group.isOpen ? "Thinking ..." : durationLabel ? `Thought for ${durationLabel}` : "Thought";

  return (
    // role="group" on the outer Box: the content Text uses _groupHover so it
    // greys out whenever the user hovers anywhere on the block — but only the
    // content text greys, never the header label (no _groupHover on header).
    <Box w="100%" borderRadius="md" px={1} py={0.5}>
      {/* Header: [lightbulb] [label + optional chevron, left-aligned] */}
      <HStack
        align="center"
        spacing={1}
        cursor={hasContent ? "pointer" : "default"}
        onClick={hasContent ? () => setIsExpanded(v => !v) : undefined}
        _hover={hasContent ? { bg: "gray.50" } : undefined}
        borderRadius="md"
        pr={1}
        py={0.5}
      >
        {/* Fixed-width lightbulb column matching tool-run icon column */}
        <Flex w="20px" justify="center" align="center" flexShrink={0} mr={2}>
          <Icon as={AppIcons.lightbulb} boxSize={3.5} color="gray.400" />
        </Flex>
        {/* Label + chevron sit together, left-aligned — no flex stretch */}
        <HStack spacing={1.5} align="center" flex={0}>
          <Text fontSize="sm" color="gray.700" fontWeight="500" mb={0} lineHeight="1.25" whiteSpace="nowrap">
            {label}
          </Text>
          {hasContent && (
            <Icon
              as={isExpanded ? AppIcons.chevronDown : AppIcons.chevronRight}
              boxSize={2.5}
              color="gray.500"
              flexShrink={0}
            />
          )}
        </HStack>
      </HStack>
      <Collapse in={isExpanded} animateOpacity>
        <Box
          ml={7}
          mr={2}
          pl={3}
          pt={1}
          pb={2}
          borderLeft="2px solid"
          borderColor="gray.200"
          maxH="240px"
          overflowY="auto"
          borderRadius="sm"
          _hover={{ bg: "gray.50" }}
          css={{
            "&::-webkit-scrollbar": { width: "3px" },
            "&::-webkit-scrollbar-thumb": {
              background: "var(--chakra-colors-gray-300)",
              borderRadius: "full",
            },
          }}
        >
          <MarkdownRenderer content={group.text ?? ""} fontSize="0.75rem" isReasoning />
        </Box>
      </Collapse>
    </Box>
  );
};

export default React.memo(ThoughtBlock);
